import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface WorkIdActionsMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Optional tooltip (e.g. why a row is disabled) */
  title?: string;
  /** Bootstrap icon class - the same icon the table's RowActions kebab uses for this action. */
  icon?: string;
}

interface WorkIdActionsMenuProps {
  left: number;
  top: number;
  onClose: () => void;
  items: WorkIdActionsMenuItem[];
}

/**
 * Renders the Work ID actions menu via a portal to document.body,
 * so it is not clipped by ancestor overflow containers.
 */
const WorkIdActionsMenu: React.FC<WorkIdActionsMenuProps> = ({
  left,
  top,
  onClose,
  items
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const menuWidth = 220;
  const menuHeight = Math.min(items.length * 44 + 16, 400);

  let adjustedLeft = left;
  let adjustedTop = top;

  if (left + menuWidth > viewportWidth - 10) {
    adjustedLeft = viewportWidth - menuWidth - 10;
  }
  if (left < 10) {
    adjustedLeft = 10;
  }
  if (top + menuHeight > viewportHeight - 10) {
    adjustedTop = viewportHeight - menuHeight - 10;
  }
  if (top < 10) {
    adjustedTop = 10;
  }

  const menu = (
    <>
      <div
        className="tw-fixed tw-w-full tw-h-full tw-top-0 tw-left-0"
        onClick={onClose}
        style={{ zIndex: 20000 }}
        aria-hidden="true"
      />
      <div
        ref={menuRef}
        className="tw-fixed tw-bg-surface tw-shadow tw-rounded tw-py-2"
        style={{
          left: `${adjustedLeft}px`,
          top: `${adjustedTop}px`,
          zIndex: 20001,
          minWidth: `${menuWidth}px`,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          border: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        {items.map((item, idx) => (
          <button
            key={idx}
            className="tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left disabled:tw-opacity-50 [@media(hover:hover)]:hover:tw-bg-surface-hover"
            type="button"
            title={item.title}
            onClick={() => {
              if (!item.disabled) {
                item.onClick();
                onClose();
              }
            }}
            disabled={item.disabled}
            style={{
              lineHeight: '1.4',
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }}
          >
            {item.icon && <i className={`bi ${item.icon} tw-w-4 tw-shrink-0 tw-text-content-secondary`} aria-hidden="true" />}
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </>
  );

  return createPortal(menu, document.body);
};

export default WorkIdActionsMenu;
